// Shared tracking on/off/paused state for the Trip + Route screens.
//
// Phase model (OTA-safe additive change):
//   "off"     — not recording. No Live Activity. No GPS watcher.
//   "active"  — recording. Watcher running, Live Activity live.
//   "paused"  — recording session preserved, watcher paused, Live Activity
//               kept alive. startedAt unchanged, tripId unchanged.
//
// Legacy callers using `useTrackingState()` / `getTrackingState()` continue to
// receive a boolean — `true` when the phase is active OR paused — so screens
// that only render "is the user currently inside a tracking session?" don't
// need to change. New callers can use `useTrackingPhase()` for the tri-state.

import { useEffect, useState } from "react";
import { kv } from "./kv";
import { liveActivityController } from "@/lib/live-activity/controller";

export const TRACKING_KEY = "mtsync.tracking";
export const TRACKING_STARTED_KEY = "mtsync.tracking.startedAt";
export const TRACKING_PHASE_KEY = "mtsync.tracking.phase"; // "active" | "paused"
const EVENT = "mtsync:tracking-changed";

export type TrackingPhase = "off" | "active" | "paused";

type Listener = (on: boolean, startedAt: string | null, phase: TrackingPhase) => void;
const listeners = new Set<Listener>();

function emit(phase: TrackingPhase, startedAt: string | null) {
  const on = phase !== "off";
  for (const l of listeners) {
    try { l(on, startedAt, phase); } catch {}
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(EVENT, { detail: { on, startedAt, phase } }));
  }
}

export async function setTrackingState(on: boolean): Promise<void> {
  await kv.set(TRACKING_KEY, on ? "1" : "0");
  let startedAt: string | null = null;
  if (on) {
    startedAt = new Date().toISOString();
    await kv.set(TRACKING_STARTED_KEY, startedAt);
    await kv.set(TRACKING_PHASE_KEY, "active");
  } else {
    await kv.set(TRACKING_STARTED_KEY, "");
    await kv.set(TRACKING_PHASE_KEY, "");
  }
  emit(on ? "active" : "off", startedAt);

  try {
    liveActivityController.onTrackingChanged(on, startedAt);
  } catch (e) {
    console.warn("[live-activity] controller call failed (non-fatal)", e);
  }
}

/**
 * Pause / resume the current tracking session.
 *
 * Does NOT touch `mtsync.tracking` (stays "1"), `mtsync.tracking.startedAt`,
 * the active watch's tripId, the outbox listener, or the Live Activity. Only
 * the phase flag flips. Pause stops the GPS watcher but keeps the trip
 * session alive; Resume re-arms the watcher. Live Activity is informed via
 * the controller so the Lock Screen / Dynamic Island pill updates its
 * isPaused state without being dismissed.
 */
export async function setTrackingPaused(paused: boolean): Promise<void> {
  const cur = await kv.get(TRACKING_KEY);
  if (cur !== "1") {
    console.warn(`[tracking-state] setTrackingPaused ignored — not currently tracking (key=${cur ?? "null"})`);
    return;
  }
  await kv.set(TRACKING_PHASE_KEY, paused ? "paused" : "active");
  const startedAt = await getTrackingStartedAt();
  const phase: TrackingPhase = paused ? "paused" : "active";
  console.info(`[tracking-state] phase=${phase} startedAt=${startedAt ?? "null"}`);
  emit(phase, startedAt);
  try {
    liveActivityController.onTrackingPaused(paused);
  } catch (e) {
    console.warn("[live-activity] controller pause call failed (non-fatal)", e);
  }
}

export async function getTrackingState(): Promise<boolean> {
  const v = await kv.get(TRACKING_KEY);
  return v === "1";
}

export async function getTrackingPhase(): Promise<TrackingPhase> {
  const on = await kv.get(TRACKING_KEY);
  if (on !== "1") return "off";
  const phase = await kv.get(TRACKING_PHASE_KEY);
  return phase === "paused" ? "paused" : "active";
}

export async function getTrackingStartedAt(): Promise<string | null> {
  const v = await kv.get(TRACKING_STARTED_KEY);
  return v && v.length > 0 ? v : null;
}

/** Synchronous best-effort phase peek for guards (uses localStorage fallback). */
export function getTrackingPhaseSync(): TrackingPhase {
  try {
    if (typeof localStorage === "undefined") return "off";
    const on = localStorage.getItem(TRACKING_KEY);
    if (on !== "1") return "off";
    const phase = localStorage.getItem(TRACKING_PHASE_KEY);
    return phase === "paused" ? "paused" : "active";
  } catch {
    return "off";
  }
}

/** Subscribe to tracking-on/off changes from any screen. */
export function useTrackingState(): boolean {
  const [on, setOn] = useState(false);

  useEffect(() => {
    let alive = true;
    void getTrackingState().then((v) => { if (alive) setOn(v); });

    const localListener: Listener = (v) => setOn(v);
    listeners.add(localListener);

    const winListener = (e: Event) => {
      const detail = (e as CustomEvent<{ on: boolean; startedAt: string | null }>).detail;
      if (detail && typeof detail.on === "boolean") setOn(detail.on);
    };
    const storageListener = (e: StorageEvent) => {
      if (e.key === TRACKING_KEY) setOn(e.newValue === "1");
    };
    if (typeof window !== "undefined") {
      window.addEventListener(EVENT, winListener);
      window.addEventListener("storage", storageListener);
    }

    return () => {
      alive = false;
      listeners.delete(localListener);
      if (typeof window !== "undefined") {
        window.removeEventListener(EVENT, winListener);
        window.removeEventListener("storage", storageListener);
      }
    };
  }, []);

  return on;
}

/** Subscribe to the tri-state phase. */
export function useTrackingPhase(): TrackingPhase {
  const [phase, setPhase] = useState<TrackingPhase>("off");

  useEffect(() => {
    let alive = true;
    void getTrackingPhase().then((p) => { if (alive) setPhase(p); });

    const localListener: Listener = (_on, _s, p) => setPhase(p);
    listeners.add(localListener);

    const winListener = (e: Event) => {
      const detail = (e as CustomEvent<{ phase: TrackingPhase }>).detail;
      if (detail?.phase) setPhase(detail.phase);
    };
    const storageListener = (e: StorageEvent) => {
      if (e.key === TRACKING_PHASE_KEY || e.key === TRACKING_KEY) {
        void getTrackingPhase().then((p) => setPhase(p));
      }
    };
    if (typeof window !== "undefined") {
      window.addEventListener(EVENT, winListener);
      window.addEventListener("storage", storageListener);
    }

    return () => {
      alive = false;
      listeners.delete(localListener);
      if (typeof window !== "undefined") {
        window.removeEventListener(EVENT, winListener);
        window.removeEventListener("storage", storageListener);
      }
    };
  }, []);

  return phase;
}

/** Subscribe to the ISO timestamp at which the current tracking session began. */
export function useTrackingStartedAt(): string | null {
  const [startedAt, setStartedAt] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void getTrackingStartedAt().then((v) => { if (alive) setStartedAt(v); });

    const localListener: Listener = (_on, s) => setStartedAt(s);
    listeners.add(localListener);

    const winListener = (e: Event) => {
      const detail = (e as CustomEvent<{ on: boolean; startedAt: string | null }>).detail;
      if (detail) setStartedAt(detail.startedAt);
    };
    const storageListener = (e: StorageEvent) => {
      if (e.key === TRACKING_STARTED_KEY) {
        setStartedAt(e.newValue && e.newValue.length > 0 ? e.newValue : null);
      }
    };
    if (typeof window !== "undefined") {
      window.addEventListener(EVENT, winListener);
      window.addEventListener("storage", storageListener);
    }

    return () => {
      alive = false;
      listeners.delete(localListener);
      if (typeof window !== "undefined") {
        window.removeEventListener(EVENT, winListener);
        window.removeEventListener("storage", storageListener);
      }
    };
  }, []);

  return startedAt;
}
